import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Eye, ArrowLeft, AlertCircle } from 'lucide-react';
import { useProject } from '../hooks/useProject';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/ui/Button';
import AdminContentEditor from '../components/admin/AdminContentEditor';

export default function AdminPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { project, loading, error } = useProject(slug);
  const { isAdminAuthenticated } = useAuth();
  const [editedContent, setEditedContent] = useState(null);
  const [editedFormules, setEditedFormules] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    if (project?.content) {
      setEditedContent(JSON.parse(JSON.stringify(project.content)));
    }
    if (project?.formules) {
      setEditedFormules(JSON.parse(JSON.stringify(project.formules)));
    }
  }, [project]);

  const handleSave = async () => {
    setSaveStatus('saving');

    try {
      // Generate the updated content.js file
      const contentFile = `export const content = ${JSON.stringify(editedContent, null, 2)};`;

      // Generate the updated formules.js file
      const formulesFile = `export const formules = ${JSON.stringify(editedFormules, null, 2)};

// Données pour le tableau comparatif
export const comparison = ${JSON.stringify(project.comparison, null, 2)};

// Calculateur d'économie
export const savings = ${JSON.stringify(project.savings, null, 2)};`;

      // Download content.js
      const contentBlob = new Blob([contentFile], { type: 'text/javascript' });
      const contentUrl = URL.createObjectURL(contentBlob);
      const contentLink = document.createElement('a');
      contentLink.href = contentUrl;
      contentLink.download = `content-${slug}-${Date.now()}.js`;
      document.body.appendChild(contentLink);
      contentLink.click();
      document.body.removeChild(contentLink);
      URL.revokeObjectURL(contentUrl);

      // Small delay before second download
      await new Promise(resolve => setTimeout(resolve, 500));

      // Download formules.js
      const formulesBlob = new Blob([formulesFile], { type: 'text/javascript' });
      const formulesUrl = URL.createObjectURL(formulesBlob);
      const formulesLink = document.createElement('a');
      formulesLink.href = formulesUrl;
      formulesLink.download = `formules-${slug}-${Date.now()}.js`;
      document.body.appendChild(formulesLink);
      formulesLink.click();
      document.body.removeChild(formulesLink);
      URL.revokeObjectURL(formulesUrl);

      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error('Error saving:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handlePreview = () => {
    navigate(`/projet/${slug}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-secondary-200">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
          <h2 className="text-2xl font-bold text-white mb-4">Erreur</h2>
          <p className="text-secondary-200 mb-8">{error || 'Projet introuvable'}</p>
          <Button onClick={() => navigate('/')}>
            <ArrowLeft size={18} />
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  // Check admin authentication
  if (!isAdminAuthenticated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
          <h2 className="text-2xl font-bold text-white mb-4">Accès Refusé</h2>
          <p className="text-secondary-200 mb-8">
            Vous devez être connecté en tant qu'administrateur pour accéder à cette page.
          </p>
          <Button onClick={() => navigate('/')}>
            <ArrowLeft size={18} />
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  if (!editedContent || !editedFormules) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-secondary-900 border-b border-secondary-700 shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/projet/${slug}`)}
                className="text-secondary-200 hover:text-white transition"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-white">Éditeur de Contenu</h1>
                <p className="text-sm text-secondary-400">{project.title}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {saveStatus && (
                <div className={`text-sm px-3 py-1 rounded ${
                  saveStatus === 'saving' ? 'bg-blue-900/30 text-blue-400' :
                  saveStatus === 'success' ? 'bg-green-900/30 text-green-400' :
                  'bg-red-900/30 text-red-400'
                }`}>
                  {saveStatus === 'saving' ? 'Téléchargement...' :
                   saveStatus === 'success' ? '✓ Fichier téléchargé' :
                   '✗ Erreur'}
                </div>
              )}

              <Button variant="outline" onClick={handlePreview} icon={<Eye size={18} />}>
                Prévisualiser
              </Button>

              <Button onClick={handleSave} icon={<Save size={18} />}>
                Télécharger les fichiers
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content Editor */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6 bg-blue-900/20 border border-blue-500 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-blue-400 flex-shrink-0 mt-1" size={20} />
            <div className="text-sm text-blue-200">
              <p className="font-semibold mb-1">Comment utiliser cet éditeur :</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Modifiez le contenu et les prix dans les champs ci-dessous</li>
                <li>Cliquez sur "Télécharger les fichiers" pour générer content.js et formules.js</li>
                <li>Remplacez les fichiers dans <code className="bg-black/50 px-1 rounded">src/projects/{slug}/</code></li>
                <li>Rechargez la page pour voir les modifications</li>
              </ol>
            </div>
          </div>
        </div>

        <AdminContentEditor
          content={editedContent}
          onContentChange={setEditedContent}
          formules={editedFormules}
          onFormulesChange={setEditedFormules}
        />
      </main>
    </div>
  );
}
